/**
 * Utility functions for generating colors for charts
 */

// Array of bright, visually distinct colors with high contrast
export const CHART_COLORS = [
  // Blues
  '#003087', // Primary Blue (Chola)
  '#0066CC', // Medium Blue
  '#1A4DAD', // Royal Blue
  '#36A2EB', // Bright Blue
  '#19b5fe', // Sky Blue
  '#1976d2', // Material Blue
  '#1565c0', // Deep Blue
  
  // Purples
  '#9966FF', // Bright Purple
  '#673ab7', // Deep Purple
  '#7e57c2', // Medium Purple
  '#5e35b1', // Rich Purple
  
  // Greens & Teals
  '#4BC0C0', // Bright Teal
  '#26c6da', // Light Cyan
  '#00acc1', // Medium Cyan
  '#26a69a', // Teal
  '#00897b', // Medium Teal
  
  // Oranges & Yellows
  '#FF9F40', // Bright Orange
  '#FF7043', // Medium Orange
  '#F4511E', // Dark Orange
  '#FFB81C', // Chola Yellow
  
  // Greys
  '#475569', // Dark Grey
  '#64748B', // Medium Grey
  '#94A3B8', // Light Grey
  
  // Additional Colors
  '#4D5382', // Indigo
  '#5C6BC0', // Indigo Blue
  '#3F51B5', // Indigo
  '#009688', // Teal
  '#00796B', // Teal Darker
  '#3949AB', // Indigo
  '#7986CB', // Light Indigo
  '#8E24AA', // Purple
];

// Primary colors for charts - a curated selection of the most vibrant colors
// Removed all red colors and replaced with blues, purples, teals, and other non-red colors
export const PRIMARY_CHART_COLORS = [
  '#003087', // Primary Blue (Chola)
  '#0066CC', // Medium Blue
  '#36A2EB', // Bright Blue
  '#4BC0C0', // Teal
  '#9966FF', // Purple
  '#FF9F40', // Orange
  '#FFB81C', // Chola Yellow
  '#1A4DAD', // Royal Blue
  '#26c6da', // Light Cyan
  '#7e57c2', // Medium Purple
  '#5C6BC0', // Indigo Blue
  '#00897b', // Medium Teal
  '#673ab7', // Deep Purple
  '#4D5382', // Indigo
  '#3949AB', // Indigo
];

// Color families for organizing colors
const COLOR_FAMILIES = {
  blues: [0, 6],     // Indices for blues in CHART_COLORS
  purples: [7, 10],  // Indices for purples
  greens: [11, 15],  // Indices for greens & teals
  oranges: [16, 19], // Indices for oranges & yellows
  greys: [20, 22],   // Indices for greys
  additional: [23, 31] // Indices for additional colors
};

// Get a random color from the CHART_COLORS array
export const getRandomColor = (): string => {
  return CHART_COLORS[Math.floor(Math.random() * CHART_COLORS.length)];
};

// Get a random color from a specific color family
export const getRandomColorFromFamily = (family: keyof typeof COLOR_FAMILIES): string => {
  const [start, end] = COLOR_FAMILIES[family];
  const index = start + Math.floor(Math.random() * (end - start + 1));
  return CHART_COLORS[index];
};

// Get a sequence of distinct colors for charts
export const getDistinctColors = (count: number): string[] => {
  // If count is small, use the primary colors
  if (count <= PRIMARY_CHART_COLORS.length) {
    return PRIMARY_CHART_COLORS.slice(0, count);
  }
  
  // For larger counts, ensure we get a good mix of colors from different families
  const result: string[] = [...PRIMARY_CHART_COLORS]; // Start with primary colors
  
  // Add more colors as needed
  while (result.length < count) {
    // Cycle through color families to ensure variety
    const families = Object.keys(COLOR_FAMILIES) as Array<keyof typeof COLOR_FAMILIES>;
    for (const family of families) {
      if (result.length < count) {
        const color = getRandomColorFromFamily(family);
        // Avoid duplicates
        if (!result.includes(color)) {
          result.push(color);
        }
      } else {
        break;
      }
    }
    
    // If we still need more colors and have exhausted all families without duplicates,
    // just add random colors
    if (result.length < count) {
      let color = getRandomColor();
      while (result.length < count) {
        if (!result.includes(color)) {
          result.push(color);
        }
        color = getRandomColor();
      }
    }
  }
  
  return result;
};

// Generate a consistent color for a specific key
export const getConsistentColor = (key: string): string => {
  // Generate a hash code from the string
  let hash = 0;
  for (let i = 0; i < key.length; i++) {
    hash = key.charCodeAt(i) + ((hash << 5) - hash);
  }
  
  // Use the hash to pick a color from our array
  const index = Math.abs(hash) % PRIMARY_CHART_COLORS.length;
  return PRIMARY_CHART_COLORS[index];
};

// Generate a color set for a key (for backward compatibility)
export const getColorSet = (key: string): { main: string; gradient: string[] } => {
  const mainColor = getConsistentColor(key);
  // Return the same color for both main and gradient to avoid using gradients
  return {
    main: mainColor,
    gradient: [mainColor, mainColor]
  };
};

// Specific colors for distribution charts - lighter and more vibrant
export const DISTRIBUTION_CHART_COLORS = [
  '#64B5F6', // Bright Sky Blue
  '#81C784', // Light Green
  '#FF6E00', // Amber Yellow
  '#4DD0E1', // Light Cyan
  '#BA68C8', // Light Purple
  '#FF8A65', // Light Coral
  '#A5D6A7', // Pale Green
  '#90CAF9', // Baby Blue
  '#F48FB1', // Pink
  '#80DEEA', // Turquoise
  '#CE93D8', // Lavender
  '#FFB74D', // Light Orange
  '#9FA8DA', // Light Indigo
  '#80CBC4', // Mint
  '#BCAAA4', // Light Brown
  '#B39DDB', // Light Violet
  '#FFCC80', // Peach
  '#C5E1A5', // Lime
  '#EF9A9A', // Salmon
  '#FFF176', // Light Yellow
  '#E6EE9C', // Pale Lime
  '#81D4FA', // Light Blue
  '#F8BBD0', // Light Pink
  '#D7CCC8', // Light Taupe
  '#FFAB91', // Light Terracotta
  '#B2DFDB', // Aqua
  '#FFAB00', // Amber
  '#DCE775', // Lime Green
  '#F06292', // Dark Pink
  '#AED581', // Light Lime
];

// Function to get a distribution chart color
export const getDistributionChartColor = (index: number): string => {
  return DISTRIBUTION_CHART_COLORS[index % DISTRIBUTION_CHART_COLORS.length];
};

// Get a sequence of distribution chart colors
export const getDistributionColors = (count: number): string[] => {
  return Array.from({ length: count }, (_, i) => getDistributionChartColor(i));
}; 